import React from "react";
import { motion } from "framer-motion";
import {
  FaUserTie,
  FaCheck,
  FaIdCard,
  FaTaxi,
  FaSearch,
  FaPlus,
  FaEnvelope,
  FaPhoneAlt,
  FaEdit,
  FaCog,
  FaTrash,
  FaCar,
  FaTruckMoving,
} from "react-icons/fa";
import moment from "moment";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner"; 

function Drivers({
  driversData,
  transfersData,
  driverSearch,
  setDriverSearch,
  driverVehicleTypeFilter,
  setDriverVehicleTypeFilter,
  handleDriverCreate,
  handleDriverEdit,
  creatingDriver,
  editingDriver,
  handleDriverPasswordChange,
  changingDriverPassword,
  handleDriverDelete,
  deletingDriverId,
}) {
  return (
    <div className="space-y-6">
      {/* Drivers Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
              <FaUserTie className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Total Drivers</p>
              <p className="text-2xl font-bold text-white">
                {driversData.length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
              <FaCheck className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Active</p>
              <p className="text-2xl font-bold text-white">
                {driversData.filter((d) => d.isActive).length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
              <FaIdCard className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">
                License Expiring Soon
              </p>
              <p className="text-2xl font-bold text-white">
                {
                  driversData.filter((d) => {
                    const daysUntilExpiry = Math.ceil(
                      (new Date(d.licenseExpiry) - new Date()) /
                        (1000 * 60 * 60 * 24)
                    );
                    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                  }).length
                }
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
              <FaTaxi className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">On Duty</p>
              <p className="text-2xl font-bold text-white">
                {
                  transfersData.filter(
                    (t) =>
                      t.assignedDriver &&
                      (t.status === "confirmed" || t.status === "pending") &&
                      new Date(t.pickUpDate) >= new Date()
                  ).length
                }
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search drivers..."
              value={driverSearch}
              onChange={(e) => setDriverSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>

          <div>
            <select
              value={driverVehicleTypeFilter}
              onChange={(e) => setDriverVehicleTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="all">All Vehicle Types</option>
              <option value="car">Car</option>
              <option value="minivan">Mini Van</option>
              <option value="van">Van</option>
              <option value="minibus">Mini Bus</option>
            </select>
          </div>

          <div>
            <motion.button
              onClick={handleDriverCreate}
              disabled={creatingDriver}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg hover:from-purple-700 hover:to-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
            >
              {creatingDriver ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Creating...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <FaPlus className="mr-2" />
                  <span>Add New Driver</span>
                </div>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Drivers List */}
      <div className="grid grid-cols-1 gap-6">
        {driversData
          .filter((driver) => {
            const matchesSearch =
              (driver.name + " " + driver.surname)
                .toLowerCase()
                .includes(driverSearch.toLowerCase()) ||
              driver.email.toLowerCase().includes(driverSearch.toLowerCase()) ||
              driver.phone.toLowerCase().includes(driverSearch.toLowerCase()) ||
              driver.licenseNumber
                .toLowerCase()
                .includes(driverSearch.toLowerCase());

            const matchesVehicleType =
              driverVehicleTypeFilter === "all" ||
              driver.vehicleTypes.includes(driverVehicleTypeFilter);

            return matchesSearch && matchesVehicleType;
          })
          .sort((a, b) => {
            // Sort by active status first, then by name
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return a.name.localeCompare(b.name);
          })
          .map((driver) => {
            const daysUntilLicenseExpiry = Math.ceil(
              (new Date(driver.licenseExpiry) - new Date()) /
                (1000 * 60 * 60 * 24)
            );

            const isLicenseExpired = daysUntilLicenseExpiry <= 0;
            const isLicenseExpiringSoon =
              daysUntilLicenseExpiry > 0 && daysUntilLicenseExpiry <= 30;

            // Find current assignments for this driver
            const currentAssignments = transfersData.filter(
              (transfer) =>
                transfer.assignedDriver &&
                transfer.assignedDriver._id === driver._id &&
                (transfer.status === "confirmed" ||
                  transfer.status === "pending") &&
                new Date(transfer.pickUpDate) >= new Date()
            );

            return (
              <motion.div
                key={driver._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 ${
                  !driver.isActive ? "opacity-75" : ""
                }`}
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    {/* Driver Info */}
                    <div className="flex items-start space-x-4">
                      <div
                        className={`${
                          driver.isActive ? "bg-green-100" : "bg-gray-100"
                        } rounded-full p-3`}
                      >
                        <FaUserTie
                          className={`w-6 h-6 ${
                            driver.isActive ? "text-green-600" : "text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {driver.name} {driver.surname}
                          </h3>
                          {isLicenseExpired && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              License Expired
                            </span>
                          )}
                          {isLicenseExpiringSoon && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              License Expiring Soon
                            </span>
                          )}
                          {!driver.isActive && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:space-x-4">
                          <p className="text-sm text-gray-500 flex items-center">
                            <FaEnvelope className="w-4 h-4 mr-1" />{" "}
                            {driver.email}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center">
                            <FaPhoneAlt className="w-4 h-4 mr-1" />{" "}
                            {driver.phone}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => handleDriverEdit(driver._id)}
                        disabled={editingDriver === driver._id}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        {editingDriver === driver._id ? (
                          <span className="flex items-center">
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Editing...</span>
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <FaEdit className="mr-2" />
                            Edit
                          </span>
                        )}
                      </button>

                      <button
                        onClick={() => handleDriverPasswordChange(driver._id)}
                        disabled={changingDriverPassword === driver._id}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                      >
                        {changingDriverPassword === driver._id ? (
                          <span className="flex items-center">
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Updating...</span>
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <FaCog className="mr-2" />
                            Change Password
                          </span>
                        )}
                      </button>

                      <button
                        onClick={() => handleDriverDelete(driver._id)}
                        disabled={deletingDriverId === driver._id}
                        className="inline-flex items-center px-3 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Driver Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* License Info */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        License Information
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                              License Number:
                            </span>
                            <span className="font-medium">
                              {driver.licenseNumber}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Expiry Date:</span>
                            <span
                              className={`font-medium ${
                                isLicenseExpired
                                  ? "text-red-600"
                                  : isLicenseExpiringSoon
                                  ? "text-yellow-600"
                                  : ""
                              }`}
                            >
                              {new Date(
                                driver.licenseExpiry
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Status:</span>
                            <span
                              className={`font-medium ${
                                isLicenseExpired
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {isLicenseExpired
                                ? "Expired"
                                : daysUntilLicenseExpiry <= 30
                                ? `Expires in ${daysUntilLicenseExpiry} days`
                                : "Valid"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Types */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        Vehicle Types
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-2">
                          {driver.vehicleTypes.map((type) => (
                            <span
                              key={type}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {type === "car" && <FaCar className="mr-1" />}
                              {type === "minivan" && (
                                <FaTaxi className="mr-1" />
                              )}
                              {type === "van" && (
                                <FaTruckMoving className="mr-1" />
                              )}
                              {type === "minibus" && (
                                <FaTruckMoving className="mr-1" />
                              )}
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Current Assignments */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        Current Assignments
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {currentAssignments.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No current assignments
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {currentAssignments.slice(0, 2).map((transfer) => (
                              <div
                                key={transfer._id}
                                className="text-sm border-l-2 border-blue-500 pl-3"
                              >
                                <p className="font-medium">
                                  {transfer.pickupLocation} →{" "}
                                  {transfer.dropoffLocation}
                                </p>
                                <p className="text-gray-500">
                                  {moment(transfer.pickUpDate).format(
                                    "DD/MM/YYYY"
                                  )}{" "}
                                  at {transfer.pickupHours}:
                                  {transfer.pickupMinutes}
                                </p>
                              </div>
                            ))}
                            {currentAssignments.length > 2 && (
                              <p className="text-xs text-blue-600 font-medium">
                                {currentAssignments.length - 2} more assignments
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {driver.notes && (
                    <div className="mt-6 bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        Notes
                      </h4>
                      <p className="text-sm text-yellow-700">{driver.notes}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
      </div>
    </div>
  );
}

export default Drivers;
